import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, catchError, map, Observable, of, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

import { AdminManagerSearchModel, BranchModel, PreAuthorisationModel, RegionModel } from '@lmsModels';

import { RETRY_ALLOWED } from '../interceptors/http-error.interceptor';

@Injectable({
  providedIn: 'root',
})
export class BranchService {

  private _lstallBranches: Observable<BranchModel[]>;
  private _lstlmsRegions = new BehaviorSubject<RegionModel[]>([]);

  BranchAdmin_apiURL = environment.apiUrl + '/loan/api/branch';
  userAudit_apiURL = `${environment.apiUrl}/loan/api/user-audit-trail`;

  HttpParamsOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  constructor(private http: HttpClient) {}

  saveBranch(branch: any) {
    return this.http.post<any>(this.BranchAdmin_apiURL + '/saveBranch', branch, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    }).pipe(
      map((responseData) => {
        return responseData;
      })
    );
  }

  getAllBranchesByAreaId(area: any) {
    return this.http.post<any>(`${this.BranchAdmin_apiURL}/getAllBranchesByAreaId`, area, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

  deleteBranch(branch: any) {
    return this.http.post<any>(this.BranchAdmin_apiURL + '/deleteBranch', branch, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    }).pipe(
      map((responseData) => {
        return responseData;
      })
    );
  }

  saveArea(area: any) {
    return this.http.post<any>(this.BranchAdmin_apiURL + '/saveArea', area, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    }).pipe(
      map((responseData) => {
        return responseData;
      })
    );
  }

  deleteArea(area: any) {
    return this.http.post<any>(this.BranchAdmin_apiURL + '/deleteArea', area, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    }).pipe(
      map((responseData) => {
        return responseData;
      })
    );
  }

  saveRegion(region: any) {
    return this.http.post<any>(this.BranchAdmin_apiURL + '/saveRegion', region, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    }).pipe(
      map((responseData) => {
        return responseData;
      })
    );
  }

  createRegion(region: any) {
    return this.http.post<any>(this.BranchAdmin_apiURL + '/createRegion', region, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    }).pipe(
      map((responseData) => {
        return responseData;
      })
    );
  }

  deleteRegion(region: any) {
    return this.http.post<any>(this.BranchAdmin_apiURL + '/deleteRegion', region, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    }).pipe(
      map((responseData) => {
        return responseData;
      })
    );
  }

  get lstBranchesObs() {
    return this._lstallBranches;
  }

  public get FinbondBranchList() {
    return this._lstallBranches.pipe(map((b) => b.values));
  }

  getAllFinbondBranches() {
    return this.http.get<any>(`${this.BranchAdmin_apiURL}/getAllBranches`).pipe(
      map((lst) => {
        return lst;
      })
    );
  }

  getAllFinbondBranchNames() {
    return this.http.get<any>(`${this.BranchAdmin_apiURL}/getAllFinbondBranchNames`);
  }

  handleBracnhListLoad(value: BranchModel[]) {
    this._lstallBranches = of(value);
    return this._lstallBranches;
  }

  // getAllFinbondBranches() {
  //   return this.http
  //   .get<BranchModel[]>(`${this.BranchAdmin_apiURL}/getAllBranches`);
  // }

  getAllBranchNames() {
    return this.http
      .get<BranchModel[]>(`${this.BranchAdmin_apiURL}/getAllBranchNames`)
      .pipe(switchMap((lst) => this.handleBracnhListLoad(lst)));
  }

  getAllAreas() {
    return this.http.get<any>(`${this.BranchAdmin_apiURL}/getAllAreas`);
  }

  getAllBranchesByRegion(region: any) {
    return this.http.get<any>(`${this.BranchAdmin_apiURL}/getAllBranchesByRegion/${region}`).pipe(
      map((res) => {
        return res;
      })
    );
  }

  getAllAreasByRegion(data: any) {
    if(!data && !data?.id) {
      return this.http.get<any>(`${this.BranchAdmin_apiURL}/getAllAreas`);
    }

    return this.http.post<any>(`${this.BranchAdmin_apiURL}/getAllAreasByRegion`, data, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
    // .pipe(
    //   map((res) => {
    //     return res;
    //   })
    // );
  }

  getAllRegions() {
    return this.http.get<RegionModel[]>(`${this.BranchAdmin_apiURL}/getAllRegions`);
  }

  get lstLmsRegionsObs() {
    return this._lstlmsRegions.asObservable();
  }

  public get LMSRegionList() {
    return this._lstlmsRegions.value;
  }

  getAllLMSRegions() {
    return this.http.get<RegionModel[]>(`${this.BranchAdmin_apiURL}/getAllRegions`);
  }

  getAllBranchesByArea(area: any) {
    return this.http.post<any>(`${this.BranchAdmin_apiURL}/getAllBranchesByAreaId`, area, {
      context: new HttpContext().set(RETRY_ALLOWED, true),
    });
  }

  getBranchByName(branchName: any) {
    return this.http.post<any>(`${this.BranchAdmin_apiURL}/getBranchByName`, branchName, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

  findLoansByDates(searchModel: any) : Observable<any> {
    return this.http.post<any>(`${this.BranchAdmin_apiURL}/findLoansByDates`, searchModel, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

  findLoansByStatus(idNumber: any) {
    return this.http.post<any>(`${this.BranchAdmin_apiURL}/findLoansByStatus`, idNumber, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

  findLoansByIdNumber(searchModel: any) {
    return this.http.post<any>(`${this.BranchAdmin_apiURL}/findLoansByIdNumber`, searchModel, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

  findLoansByClientNumber(searchModel: any) {
    return this.http.post<any>(`${this.BranchAdmin_apiURL}/findLoansByClientNumber`, searchModel, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

  findLoanCountByDates(searchModel: any) {
    return this.http.post<any>(`${this.BranchAdmin_apiURL}/findLoanCountByDates`, searchModel, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

  fetchBranchReport(startDate: string, endDate: string) {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http
      .get<any[]>(`${environment.apiUrl}/loan/api/client/fetchBranchReport`, { params });
  }

  fetchBranchReportByArea(createdOn: string, area: string) {
    const params = new HttpParams()
      .set('createdOn', createdOn)
      .set('area', area);

    return this.http
      .get<any[]>(`${environment.apiUrl}/loan/api/client/fetchBranchReportByArea`, { params });
  }

  findRegionLoanStats(searchModel: any) {
    return this.http.post<any>(`${this.BranchAdmin_apiURL}/findRegionLoanStats`, searchModel, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

  findRegionAreaLoanStats(searchModel: any) {
    return this.http.post<any>(`${this.BranchAdmin_apiURL}/findRegionAreaLoanStats`, searchModel, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

  branchPerformanceReport(searchModel: AdminManagerSearchModel) {
    return this.http.post<any>(`${this.BranchAdmin_apiURL}/branchPerformanceReport`, searchModel, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

  getAllActiveBranchesByArea(selectedArea: string) {
    return this.http.post<any>(`${this.BranchAdmin_apiURL}/getAllActiveBranchesByArea`, selectedArea, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

 findLoanById(loanId: number): Observable<PreAuthorisationModel> {
    // Validate input
    if (!loanId || isNaN(loanId)) {
        return throwError(() => new Error('Invalid loan ID'));
    }

    const params = new HttpParams().set('quoteId', loanId.toString());

    return this.http.get<PreAuthorisationModel>(
        `${this.BranchAdmin_apiURL}/findLoanById`,
        { params }
    ).pipe(
        catchError(error => {
            console.error('Error fetching loan:', error);
            return throwError(() => new Error('Failed to load loan details'));
        })
    );
}

  findAllLoans(filters: any): Observable<PreAuthorisationModel[]> {
    let params = new HttpParams();

    if (filters.loanId != null && filters.loanId !== '') {
      params = params.set('loanId', filters.loanId);
    }

    if (filters.startDate != null && filters.startDate !== '') {
      const startD = new Date(filters.startDate);
      params = params.set('startDate', `${startD.getFullYear()}-${(startD.getMonth() + 1).toString().padStart(2, '0')}-${startD.getDate().toString().padStart(2, '0')}`);
    }

    if (filters.endDate != null && filters.endDate !== '') {
      const endD = new Date(filters.endDate);
      params = params.set('endDate', `${endD.getFullYear()}-${(endD.getMonth() + 1).toString().padStart(2, '0')}-${endD.getDate().toString().padStart(2, '0')}`);
    }

    if (filters.branchName != null && filters.branchName !== '') {
      params = params.set('branchName', filters.branchName);
    }

    if (filters.loanReference != null && filters.loanReference !== '') {
      params = params.set('loanReference', filters.loanReference ?? '');
    }

    if (filters.clientNumber != null && filters.clientNumber !== '') {
      params = params.set('clientNumber', filters.clientNumber);
    }

    if (filters.idNumber != null && filters.idNumber !== '') {
      params = params.set('idNumber', filters.idNumber);
    }

    if (filters.fullName != null && filters.fullName !== '') {
      params = params.set('fullName', filters.fullName);
    }


    if (filters.loanStatus != null && filters.loanStatus !== '') {
      params = params.set('loanStatus', filters.loanStatus);
    }

    return this.http.get<PreAuthorisationModel[]>(
      `${this.BranchAdmin_apiURL}/getAllLoans`,
      { params }
    );
  }

   updateLoanStatus(payload: {quoteId: number; cancelReason?: string | null; areaManager?: string;}): Observable<any> {
       let params = new HttpParams()
      .set('quoteId', payload.quoteId);

    if (payload.cancelReason != null) {
      params = params.set('cancelReason', payload.cancelReason.toString());
    }

    if (payload.areaManager) {
      params = params.set('areaManager', payload.areaManager);
    }

    return this.http.post<any>(
      `${this.BranchAdmin_apiURL}/updateLoanStatus`,
      { context: new HttpContext().set(RETRY_ALLOWED, true) },
      { params });
  }

   //new
   setLoanStatus(searchModel: any) {
    return this.http.post<any>(`${this.BranchAdmin_apiURL}/setLoanStatus`, searchModel, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }


     fetchClientChangeLogs(startDate: any, endDate: any, branchId: any) {
    const params = new HttpParams()
    .set('startDate', startDate)
    .set('endDate', endDate)
    .set('branchId', branchId);
    return this.http.post<any>(`${environment.apiUrl}/loan/api/client/fetchClientChangeLogs`, params, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

    fetchClientChangeLogsByBranch(startDate: any, endDate: any, branchSerial: any) {
    const params = new HttpParams()
    .set('startDate', startDate)
    .set('endDate', endDate)
    .set('branchSerial', branchSerial);
    return this.http.post<any>(`${environment.apiUrl}/loan/api/client/fetchClientChangeLogsByBranch`, params, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

   fetchOverrideData(startDate: any, endDate: any, branchSerial: any) {
    const params = new HttpParams()
    .set('startDate', startDate)
    .set('endDate', endDate)
    .set('branchSerial', branchSerial);
    return this.http.post<any>(`${environment.apiUrl}/loan/api/client/fetchOverrideDataByBranch`, params, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

  getAuditTrailData(startDate: any, endDate: any, branchId: any) {
    const params = new HttpParams()
    .set('startDate', startDate)
    .set('endDate', endDate)
    .set('branchId', branchId);
    return this.http.post<any>(`${this.userAudit_apiURL}/fetchUserAuditTrail`, params, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

  findLoansByIdNumberAndDates(searchModel: AdminManagerSearchModel) {
    return this.http.post<any>(`${this.BranchAdmin_apiURL}/findLoansByIdNumberAndDates`, searchModel, {
      context: new HttpContext().set(RETRY_ALLOWED, true)
    });
  }

  fetchCompletedBranchLoans(startDate: string, endDate: string) {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http
      .get<any[]>( // Use GET, not POST
        `${environment.apiUrl}/loan/api/client/fetchCompletedBranchLoans`,
        { params }, // Pass params in options object
      );
  }


 fetchNupayLoans(startDate: string, endDate: string) {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http
      .get<any[]>( // Use GET, not POST
        `${environment.apiUrl}/loan/api/client/fetchNupayReport`,
        { params }, // Pass params in options object
      );
  }

  fetchLmsDataReport(startDate: string, endDate: string) {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http
      .get<any[]>( // Use GET, not POST
        `${environment.apiUrl}/loan/api/client/fetchLmsDataReport`,
        { params }, // Pass params in options object
      );
  }

  fetchScanningMonitoringLoans(startDate: string, endDate: string) {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http
      .get<any[]>( // Use GET, not POST
        `${environment.apiUrl}/loan/api/client/fetchScanningMonitoringReport`,
        { params }, // Pass params in options object
      );
  }
}
